/**
 * EEG Segment Cache Service
 * Sprint 4: Performance, Signal Controls & Analysis Panels
 *
 * Purpose: Client-side LRU cache for recently-viewed EEG segments
 * - IndexedDB persistence for large Arrow buffers
 * - In-memory cache for metadata and small segments
 * - LRU eviction policy with configurable size limits
 * - Automatic cache warming for predicted segments
 * - Cache hit/miss metrics for monitoring
 */

import { openDB, DBSchema, IDBPDatabase } from "idb";
import logger from "@shared/utils/logger";
import {
  buildTileCacheKey,
  resolveTileCacheIdentity,
  type TileCacheIdentity,
} from "../utils/tileCacheIdentity";

/**
 * IndexedDB schema for EEG segment cache
 */
interface EEGCacheDB extends DBSchema {
  segments: {
    // v5+: recordingId_start_end_level_speed_mode_montage_channels_zoom
    key: string;
    value: {
      key: string;
      recordingId: string;
      startMs: number;
      endMs: number;
      level: number;
      speedBucket: number;
      transportMode: EEGTileTransportMode;
      montageId: string;
      channelSetHash: string;
      zoomBucket: string;
      filterHash: string;
      gainBucket: string;
      data: ArrayBuffer; // Arrow IPC buffer
      metadata: {
        samplingRate: number;
        numChannels: number;
        channelNames: string[];
        cached_at: number;
        size_bytes: number;
        tileRangeHeader?: string | null;
      };
      last_accessed: number;
      access_count: number;
    };
    indexes: { recordingId: string; last_accessed: number };
  };
  metadata: {
    key: string;
    value: {
      key: string;
      totalSize: number;
      hitCount: number;
      missCount: number;
      evictionCount: number;
      lastCleanup: number;
    };
  };
}

export type EEGTileTransportMode = "play" | "inspect";

export interface CacheSegment {
  recordingId: string;
  startMs: number;
  endMs: number;
  level: number;
  speedBucket: number;
  transportMode: EEGTileTransportMode;
  /** Sprint 2 identity — defaults applied when omitted. */
  identity?: TileCacheIdentity | null;
  data: ArrayBuffer;
  metadata: {
    samplingRate: number;
    numChannels: number;
    channelNames: string[];
    cached_at: number;
    size_bytes: number;
    tileRangeHeader?: string | null;
  };
}

export type { TileCacheIdentity };

export interface CachedEEGSegmentEntry {
  data: ArrayBuffer;
  metadata: CacheSegment["metadata"];
}

export interface CacheStats {
  totalSize: number;
  entryCount: number;
  hitCount: number;
  missCount: number;
  hitRate: number;
  evictionCount: number;
  lastCleanup: number;
}

export interface CacheOptions {
  /** Maximum cache size in bytes (default: 100MB) */
  maxSizeBytes?: number;

  /** Maximum number of cached segments (default: 100) */
  maxEntries?: number;

  /** Time-to-live for cache entries in ms (default: 1 hour) */
  ttlMs?: number;

  /** Enable automatic cache warming for predicted segments */
  enablePredictiveWarming?: boolean;

  /** Enable debug logging */
  debug?: boolean;
}

/**
 * EEGSegmentCache: Persistent client-side cache for EEG data segments
 * Implements LRU eviction with IndexedDB storage
 */
export class EEGSegmentCache {
  private db: IDBPDatabase<EEGCacheDB> | null = null;
  private options: Required<CacheOptions>;
  private initPromise: Promise<void> | null = null;

  // In-memory LRU tracking (fast access)
  private accessOrder: Map<string, number> = new Map();
  private currentSize: number = 0;
  private hitCount: number = 0;
  private missCount: number = 0;
  private evictionCount: number = 0;
  private lastCleanup: number = 0;

  constructor(options: CacheOptions = {}) {
    this.options = {
      maxSizeBytes: options.maxSizeBytes || 100 * 1024 * 1024, // 100MB
      maxEntries: options.maxEntries || 100,
      ttlMs: options.ttlMs || 60 * 60 * 1000, // 1 hour
      enablePredictiveWarming: options.enablePredictiveWarming ?? false,
      debug: options.debug ?? false,
    };
  }

  /**
   * Initialize IndexedDB connection
   */
  async initialize(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        this.db = await openDB<EEGCacheDB>("eeg-segment-cache", 5, {
          upgrade(db, oldVersion, _newVersion, transaction) {
            // Create segments store
            if (!db.objectStoreNames.contains("segments")) {
              const segmentStore = db.createObjectStore("segments", {
                keyPath: "key",
              });
              segmentStore.createIndex("recordingId", "recordingId", {
                unique: false,
              });
              segmentStore.createIndex("last_accessed", "last_accessed", {
                unique: false,
              });
            }

            // Create metadata store
            if (!db.objectStoreNames.contains("metadata")) {
              db.createObjectStore("metadata", { keyPath: "key" });
            }

            // v3: transport mode. v4: tile-range metadata. v5: montage /
            // channel-set / zoom identity in the cache key. Prior entries are
            // incompatible and must be dropped.
            if (oldVersion < 5) {
              transaction.objectStore("segments").clear();
              transaction.objectStore("metadata").clear();
            }
          },
        });

        // Restore size from persisted stats to avoid loading all ArrayBuffers
        // (db.getAll("segments") with ~91 tiles of 0.9 MB each blocks the main
        // thread for ~1373 ms — see [EEGTileCache] IDB violation in console).
        const persistedStats = await this.db.get("metadata", "stats");
        if (
          persistedStats != null &&
          typeof (persistedStats as Record<string, unknown>).totalSize ===
            "number"
        ) {
          this.currentSize = (persistedStats as Record<string, unknown>)
            .totalSize as number;
          this.hitCount =
            typeof (persistedStats as Record<string, unknown>).hitCount ===
            "number"
              ? ((persistedStats as Record<string, unknown>).hitCount as number)
              : 0;
          this.missCount =
            typeof (persistedStats as Record<string, unknown>).missCount ===
            "number"
              ? ((persistedStats as Record<string, unknown>).missCount as number)
              : 0;
          this.evictionCount =
            typeof (persistedStats as Record<string, unknown>).evictionCount ===
            "number"
              ? ((persistedStats as Record<string, unknown>).evictionCount as number)
              : 0;
          this.lastCleanup =
            typeof (persistedStats as Record<string, unknown>).lastCleanup ===
            "number"
              ? ((persistedStats as Record<string, unknown>).lastCleanup as number)
              : 0;
          // Rebuild access-order index from keys only (no binary data loaded).
          // Use 0 as timestamp so old entries are treated as LRU-eligible.
          const storedKeys = await this.db.getAllKeys("segments");
          for (const k of storedKeys) {
            this.accessOrder.set(k as string, 0);
          }
        } else {
          // First-ever init (no persisted size) — run full scan once.
          await this.calculateCacheSize();
        }

        this.log("Cache initialized", {
          maxSize: this.formatBytes(this.options.maxSizeBytes),
          maxEntries: this.options.maxEntries,
          currentSize: this.formatBytes(this.currentSize),
        });
      } catch (error) {
        logger.error("Failed to initialize EEG cache (cache disabled):", error);
        // Leave this.db as null — all callers guard with `if (!this.db)` and
        // degrade gracefully to cache-miss without crashing the viewer.
      }
    })();

    return this.initPromise;
  }

  /**
   * Generate cache key from segment parameters + view identity.
   */
  private getCacheKey(
    recordingId: string,
    startMs: number,
    endMs: number,
    level: number,
    speedBucket: number,
    transportMode: EEGTileTransportMode,
    identity?: TileCacheIdentity | null,
  ): string {
    return buildTileCacheKey({
      recordingId,
      startMs,
      endMs,
      level,
      speedBucket: this.normalizeSpeedBucket(speedBucket),
      transportMode: this.normalizeTransportMode(transportMode),
      identity,
    });
  }

  private normalizeSpeedBucket(speedBucket: number): number {
    const normalized = Math.trunc(Math.abs(Number(speedBucket)));
    if (!Number.isFinite(normalized) || normalized < 1) {
      return 1;
    }
    return normalized;
  }

  private normalizeTransportMode(
    transportMode: EEGTileTransportMode | string | null | undefined,
  ): EEGTileTransportMode {
    return transportMode === "play" ? "play" : "inspect";
  }

  /**
   * Get segment from cache
   * Returns null if not found or expired
   */
  async get(
    recordingId: string,
    startMs: number,
    endMs: number,
    level: number,
    speedBucket: number,
    transportMode: EEGTileTransportMode,
    identity?: TileCacheIdentity | null,
  ): Promise<ArrayBuffer | null> {
    const entry = await this.getEntry(
      recordingId,
      startMs,
      endMs,
      level,
      speedBucket,
      transportMode,
      identity,
    );

    return entry?.data ?? null;
  }

  async getEntry(
    recordingId: string,
    startMs: number,
    endMs: number,
    level: number,
    speedBucket: number,
    transportMode: EEGTileTransportMode,
    identity?: TileCacheIdentity | null,
  ): Promise<CachedEEGSegmentEntry | null> {
    await this.initialize();
    if (!this.db) return null;

    const key = this.getCacheKey(
      recordingId,
      startMs,
      endMs,
      level,
      speedBucket,
      transportMode,
      identity,
    );

    try {
      const entry = await this.db.get("segments", key);

      if (!entry) {
        await this.incrementMiss();
        this.log("Cache miss", { key });
        return null;
      }

      // Check if expired
      const now = Date.now();
      if (now - entry.metadata.cached_at > this.options.ttlMs) {
        this.log("Cache entry expired", {
          key,
          age: now - entry.metadata.cached_at,
        });
        await this.delete(key);
        await this.incrementMiss();
        return null;
      }

      // Update access tracking
      this.accessOrder.set(key, now);

      await this.incrementHit();
      this.log("Cache hit", { key, accessCount: entry.access_count });

      return {
        data: entry.data,
        metadata: entry.metadata,
      };
    } catch (error) {
      logger.error("Cache get error:", error);
      await this.incrementMiss();
      return null;
    }
  }

  /**
   * Put segment into cache with LRU eviction
   */
  async put(segment: CacheSegment): Promise<void> {
    await this.initialize();
    if (!this.db) return;

    const resolvedIdentity = resolveTileCacheIdentity(segment.identity);
    const key = this.getCacheKey(
      segment.recordingId,
      segment.startMs,
      segment.endMs,
      segment.level,
      segment.speedBucket,
      segment.transportMode,
      segment.identity,
    );

    const now = Date.now();
    const { identity: _identity, ...segmentFields } = segment;
    const entry = {
      key,
      ...segmentFields,
      montageId: resolvedIdentity.montageId,
      channelSetHash: resolvedIdentity.channelSetHash,
      zoomBucket: resolvedIdentity.zoomBucket,
      filterHash: resolvedIdentity.filterHash,
      gainBucket: resolvedIdentity.gainBucket,
      last_accessed: now,
      access_count: 1,
    };

    const entrySize = segment.data.byteLength;

    try {
      const existing = await this.db.get("segments", key);
      if (existing) {
        this.currentSize = Math.max(
          0,
          this.currentSize - existing.data.byteLength,
        );
        this.accessOrder.delete(key);
      }

      // Check if eviction needed (with safety limit to prevent infinite loop)
      let evictionAttempts = 0;
      const maxEvictionAttempts = this.options.maxEntries + 1;
      while (
        (this.currentSize + entrySize > this.options.maxSizeBytes ||
          this.accessOrder.size >= this.options.maxEntries) &&
        evictionAttempts < maxEvictionAttempts &&
        this.accessOrder.size > 0
      ) {
        await this.evictLRU();
        evictionAttempts++;
      }

      // Store segment
      await this.db.put("segments", entry);
      this.currentSize += entrySize;
      this.accessOrder.set(key, now);
      // Persist updated size so that the next init can skip the expensive
      // getAll("segments") scan (fire-and-forget — non-blocking).
      this.persistStatsSnapshot().catch(() => {});

      this.log("Cache put", {
        key,
        size: this.formatBytes(entrySize),
        totalSize: this.formatBytes(this.currentSize),
      });
    } catch (error) {
      logger.error("Cache put error:", error);
    }
  }

  /**
   * Delete specific cache entry
   */
  async delete(key: string): Promise<void> {
    if (!this.db) return;

    try {
      const entry = await this.db.get("segments", key);
      if (entry) {
        this.currentSize -= entry.data.byteLength;
        await this.db.delete("segments", key);
        this.accessOrder.delete(key);
        this.persistStatsSnapshot().catch(() => {});
        this.log("Cache delete", { key });
      }
    } catch (error) {
      logger.error("Cache delete error:", error);
    }
  }

  /**
   * Persist currentSize to the stats metadata record so that initialize()
   * can skip the expensive getAll("segments") scan on next page load.
   */
  private async persistStatsSnapshot(): Promise<void> {
    if (!this.db) return;
    try {
      await this.db.put("metadata", {
        key: "stats",
        totalSize: this.currentSize,
        hitCount: this.hitCount,
        missCount: this.missCount,
        evictionCount: this.evictionCount,
        lastCleanup: this.lastCleanup,
      });
    } catch {
      // Non-fatal — worst case is a slower init on next load
    }
  }

  /**
   * Evict least recently used entry
   */
  private async evictLRU(): Promise<void> {
    if (!this.db) return;

    // Find LRU entry from in-memory tracking
    let lruKey: string | null = null;
    let lruTime = Infinity;

    for (const [key, time] of this.accessOrder.entries()) {
      if (time < lruTime) {
        lruTime = time;
        lruKey = key;
      }
    }

    if (lruKey) {
      this.log("Evicting LRU entry", { key: lruKey });
      await this.delete(lruKey);
      await this.incrementEviction();
    }
  }

  /**
   * Clear all cached segments for a recording
   */
  async clearRecording(recordingId: string): Promise<void> {
    await this.initialize();
    if (!this.db) return;

    try {
      const tx = this.db.transaction("segments", "readwrite");
      const index = tx.store.index("recordingId");
      const entries = await index.getAll(recordingId);

      for (const entry of entries) {
        await tx.store.delete(entry.key);
        this.currentSize -= entry.data.byteLength;
        this.accessOrder.delete(entry.key);
      }

      await tx.done;
      this.log("Cleared recording cache", {
        recordingId,
        count: entries.length,
      });
    } catch (error) {
      logger.error("Clear recording error:", error);
    }
  }

  /**
   * Clear all cache entries
   */
  async clear(): Promise<void> {
    await this.initialize();
    if (!this.db) return;

    try {
      await this.db.clear("segments");
      this.currentSize = 0;
      this.accessOrder.clear();
      this.log("Cache cleared");
    } catch (error) {
      logger.error("Cache clear error:", error);
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<CacheStats> {
    await this.initialize();
    if (!this.db) {
      return {
        totalSize: 0,
        entryCount: 0,
        hitCount: 0,
        missCount: 0,
        hitRate: 0,
        evictionCount: 0,
        lastCleanup: 0,
      };
    }

    try {
      const entryCount = await this.db.count("segments");
      const totalRequests = this.hitCount + this.missCount;
      const hitRate = totalRequests > 0 ? this.hitCount / totalRequests : 0;

      return {
        totalSize: this.currentSize,
        entryCount,
        hitCount: this.hitCount,
        missCount: this.missCount,
        hitRate,
        evictionCount: this.evictionCount,
        lastCleanup: this.lastCleanup,
      };
    } catch (error) {
      logger.error("Get stats error:", error);
      return {
        totalSize: 0,
        entryCount: 0,
        hitCount: 0,
        missCount: 0,
        hitRate: 0,
        evictionCount: 0,
        lastCleanup: 0,
      };
    }
  }

  /**
   * Cleanup expired entries
   */
  async cleanup(): Promise<number> {
    await this.initialize();
    if (!this.db) return 0;

    const now = Date.now();
    let cleanedCount = 0;

    try {
      const tx = this.db.transaction("segments", "readwrite");
      const allEntries = await tx.store.getAll();

      for (const entry of allEntries) {
        if (now - entry.metadata.cached_at > this.options.ttlMs) {
          await tx.store.delete(entry.key);
          this.currentSize -= entry.data.byteLength;
          this.accessOrder.delete(entry.key);
          cleanedCount++;
        }
      }

      await tx.done;

      // Update last cleanup time
      this.lastCleanup = now;
      this.persistStatsSnapshot().catch(() => {});

      this.log("Cleanup complete", { cleanedCount });
      return cleanedCount;
    } catch (error) {
      logger.error("Cleanup error:", error);
      return 0;
    }
  }

  /**
   * Calculate total cache size from IndexedDB
   */
  private async calculateCacheSize(): Promise<void> {
    if (!this.db) return;

    try {
      const allEntries = await this.db.getAll("segments");
      this.currentSize = allEntries.reduce(
        (sum, entry) => sum + entry.data.byteLength,
        0,
      );
    } catch (error) {
      logger.error("Calculate size error:", error);
      this.currentSize = 0;
    }
  }

  /**
   * Increment cache hit counter
   */
  private async incrementHit(): Promise<void> {
    this.hitCount += 1;
  }

  /**
   * Increment cache miss counter
   */
  private async incrementMiss(): Promise<void> {
    this.missCount += 1;
  }

  /**
   * Increment eviction counter
   */
  private async incrementEviction(): Promise<void> {
    this.evictionCount += 1;
    this.persistStatsSnapshot().catch(() => {});
  }

  /**
   * Format bytes for logging
   */
  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.options.debug) {
      logger.info(`[EEGSegmentCache] ${message}`, data || "");
    }
  }

  /**
   * Close database connection
   */
  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Singleton instance
let cacheInstance: EEGSegmentCache | null = null;

/**
 * Get shared cache instance
 */
export const getEEGCache = (options?: CacheOptions): EEGSegmentCache => {
  if (!cacheInstance) {
    cacheInstance = new EEGSegmentCache(options);
  }
  return cacheInstance;
};

/**
 * Reset cache instance (for testing)
 */
export const resetEEGCache = (): void => {
  if (cacheInstance) {
    cacheInstance.close();
    cacheInstance = null;
  }
};

export default EEGSegmentCache;
